import React from 'react'
import PropTypes from 'prop-types'

import Error from '@shared/components/error'
import dynamic from 'next/dynamic'
import { endTime, execTime, startTime } from '@shared/libs/metrics'
import pageGone from '@shared-submodule/utils'
const ArticleDetail = dynamic(() => import('@shared/components/articleDetail'))
const ArticleContent = dynamic(() => import('@shared/components/articleDetail/articleContent'))
const CategoryMain = dynamic(() => import('@shared/components/categoryMain'))
const CMSContent = dynamic(() => import('@shared/components/cmsContent'))
const CategoryContent = dynamic(() => import('@shared/components/categoryContent'))
const TagPlayerTeamDetail = dynamic(() => import('@shared/components/tagPlayerTeamDetail'))

function Slug({ seoData, category, cmsData, tag, article, liveArticle, latestArticles, currentSeriesData, rankingTabData }) {
  if (seoData?.eType === 'ct') {
    return <CategoryMain category={category} seoData={seoData} currentSeriesData={currentSeriesData} rankingTabData={rankingTabData} />
  } else if (seoData?.eType === 'gt' || seoData?.eType === 'p' || seoData?.eType === 't') {
    return (
      <CategoryContent
        seoData={seoData}
        category={{ ...tag?.data, oSeo: seoData }}
        showNav
        currentSeriesData={currentSeriesData}
        rankingTabData={rankingTabData}>
        {({ activeTab, changeTab }) => <TagPlayerTeamDetail seoData={seoData} tag={tag} onTabChanges={changeTab} activeTab={activeTab} />}
      </CategoryContent>
    )
  } else if (seoData?.eType === 'cms') {
    return <CMSContent data={cmsData} seoData={seoData} />
  } else if (seoData?.eType === 'ar') {
    return (
      <ArticleDetail seoData={seoData} article={article} liveArticle={liveArticle} type="article">
        <ArticleContent article={article} liveArticle={liveArticle} />
      </ArticleDetail>
    )
  }
}

Slug.propTypes = {
  seoData: PropTypes.object,
  cmsData: PropTypes.object,
  category: PropTypes.object,
  tag: PropTypes.object,
  article: PropTypes.object,
  liveArticle: PropTypes.object,
  latestArticles: PropTypes.object,
  currentSeriesData: PropTypes.object,
  rankingTabData: PropTypes.object
}

export default Error(Slug)

export async function getServerSideProps({ req, res, params, query, resolvedUrl }) {
  // If listicle page number is in URL then remove it
  const { checkPageNumberInSlug } = await import('@shared-submodule/utils')
  const slug = checkPageNumberInSlug([...params?.slug], false)?.slug?.join('/')

  try {
    if (slug?.includes('.xml')) {
      // Sitemap
      const getSiteMapData = (await import('@shared/libs/sitemap')).getSiteMapData
      await getSiteMapData(res, slug)
      return {
        props: {}
      }
    } else {
      const parallelCalls = (await import('@shared-submodule/utils')).parallelCalls
      const [queryGraphql, GET_SEO_DATA, checkRedirectionStatus, isListicleArticlePage, CURRENT_SERIES_LIST, RANKINGS, ENUM_SEO_SUBTYPE] =
        await parallelCalls([
          import('@shared/components/queryGraphql').then(module => module.default),
          import('@graphql/article/article.query').then(module => module.GET_SEO_DATA),
          import('@shared-submodule/utils').then(module => module.checkRedirectionStatus),
          import('@shared-submodule/utils').then(module => module.isListicleArticlePage),
          import('@graphql/globalwidget/rankings.query').then(module => module.CURRENT_SERIES_LIST),
          import('@graphql/globalwidget/rankings.query').then(module => module.RANKINGS),
          import('@shared-submodule/sm-enum').then(module => module.ENUM_SEO_SUBTYPE)
        ])
      const { data: seo } = await queryGraphql(GET_SEO_DATA, { input: { sSlug: slug } })
      const tabType = ENUM_SEO_SUBTYPE[seo?.getSeoData?.eSubType]

      // Check Redirection
      const { redirectStatus, eCode, returnObj, props } = checkRedirectionStatus(seo?.getSeoData)
      if (redirectStatus && props) {
        res.statusCode = eCode
        return { props }
      } else if (redirectStatus) return returnObj

      if (seo?.getSeoData?.eType === 'ct') {
        // For Simple and series category
        const getCategorySSRData = (await import('@shared/libs/category')).getCategorySSRData
        if (tabType === 'standings') res.setHeader('Cache-Control', 'public, max-age=120, s-maxage=120')
        else res.setHeader('Cache-Control', 'public, max-age=420')
        const value = await getCategorySSRData(seo?.getSeoData, tabType, false, query, resolvedUrl)
        const [currentSeriesData, rankingTabData] = await parallelCalls([
          queryGraphql(CURRENT_SERIES_LIST, { context: { headers: { 'Cache-Control': 'public, max-age=300, s-maxage=600, stale-while-revalidate=420' } } }),
          queryGraphql(RANKINGS, { input: { eMatchType: 'Tests', eRankType: 'Teams', nLimit: 5, nSkip: 0 } })
        ])
        if (value?.notFound) return value
        return {
          props: {
            seoData: seo?.getSeoData,
            category: value,
            currentSeriesData: currentSeriesData,
            rankingTabData: rankingTabData
          }
        }
      } else if (seo?.getSeoData?.eType === 'gt' || seo?.getSeoData?.eType === 'p' || seo?.getSeoData?.eType === 't') {
        // General tag, Player, Team
        const getTagData = (await import('@shared/libs/news')).getTagData
        const { data: currentSeriesData } = await queryGraphql(CURRENT_SERIES_LIST, { context: { headers: { 'Cache-Control': 'public, max-age=300, s-maxage=600, stale-while-revalidate=420' } } })
        const { data: rankingTabData } = await queryGraphql(RANKINGS, { input: { eMatchType: 'Tests', eRankType: 'Teams', nLimit: 5, nSkip: 0 } })
        res.setHeader('Cache-Control', 'public, max-age=420')

        const value = await getTagData(seo?.getSeoData)
        if (value?.notFound) return value
        return {
          props: {
            seoData: seo?.getSeoData,
            tag: value,
            currentSeriesData: currentSeriesData,
            rankingTabData: rankingTabData
          }
        }
      } else if (seo?.getSeoData?.eType === 'cms') {
        // Cms pages
        res.setHeader('Cache-Control', 'public, max-age=1200')
        const GET_CMS_PAGE = (await import('@graphql/cms/cms.query')).GET_CMS_PAGE
        const { data: cmsData } = await queryGraphql(GET_CMS_PAGE, { input: { _id: seo?.getSeoData?.iId } })
        return {
          props: {
            seoData: seo?.getSeoData,
            cmsData: cmsData
          }
        }
      } else {
        // News Article
        res.setHeader('Cache-Control', 'public, max-age=420')
        const GET_ARTICLE_DETAILS_DATA = (await import('@graphql/article/article.query')).GET_ARTICLE_DETAILS_DATA

        // const parallelCalls = (await import('@shared-submodule/utils')).parallelCalls
        // const [data] = await parallelCalls([
        //   queryGraphql(GET_ARTICLE_DETAILS, { input: { _id: seo?.getSeoData?.iId } }),
        //   queryGraphql(CURRENT_SERIES_LIST)
        // ])
        startTime('GET_ARTICLE_DETAILS_DATA')
        const { data } = await queryGraphql(GET_ARTICLE_DETAILS_DATA, { input: { _id: seo?.getSeoData?.iId } })

        if (data?.getArticleFront?.iEventId) res.setHeader('Cache-Control', 'public, must-revalidate, max-age=120, s-maxage=120')
        else res.setHeader('Cache-Control', 'public, max-age=420')
        const liveArticle = {}
        if (data.getArticleFront?.iEventId) {
          const { getLiveArticleData } = await import('@shared-submodule/sm-libs/live-blog')
          const { oLiveArticleContent, oLiveArticleAmpContent, aLiveArticleList, oLiveArticleEvent } = await getLiveArticleData(
            data?.getArticleFront
          )
          liveArticle.oLiveArticleContent = oLiveArticleContent
          liveArticle.oLiveArticleAmpContent = oLiveArticleAmpContent
          liveArticle.aLiveArticleList = aLiveArticleList
          liveArticle.oLiveArticleEvent = oLiveArticleEvent || {}
        }

        // Check current listicle page is more than total page
        const currentPage = params?.slug[params?.slug?.length - 1]
        const { redirectionRule, applyRedirection } = isListicleArticlePage(data?.getArticleFront, currentPage, slug, false)
        if (applyRedirection) return redirectionRule
        const timeTaken = endTime('GET_ARTICLE_DETAILS_DATA')

        return {
          props: {
            perf: timeTaken,
            totalServerTime: execTime(),
            seoData: seo?.getSeoData || {},
            article: data?.getArticleFront || {},
            liveArticle
          }
        }
      }
    }
  } catch (e) {
    // console.log({ e })
    res.setHeader('Cache-Control', 'no-cache')
    const handleApiError = (await import('@shared-submodule/utils')).handleApiError
    const status = handleApiError(e, resolvedUrl)
    if (status?.props?.statusCode === 410) pageGone(res)
    return status
  }
}
